import useAppContext from 'flow/hooks/useAppContext';
import React, { useState } from 'react';
import BaseInput from 'flow/components/ui-components/BaseInput';
import BaseModal from 'flow/components/ui-components/BaseModal';
import ImagePicker from 'flow/pages/messages/ImagePicker';
import {
    createRoomSession,
    deleteRoomImage,
    setRoomImage,
    updateChatSession,
} from 'flow/actions/chat-actions';
import { fromJS } from 'immutable';
import { generatePath, useHistory } from 'react-router-dom';
import { routes } from 'flow/types/routes';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import BaseTextArea from 'flow/components/ui-components/BaseTextArea';
import { roomTypes } from 'flow/pages/messages/MessageInput';
import { RoomContent } from './ManageRoomModal';
import useAutoFocus from 'flow/hooks/useAutoFocus';
import { getChatRoomAvatar } from 'flow/utils';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

const defaultValue: RoomContent = {
    attachment: null,
    title: '',
    description: '',
    imageSrc: '',
};

interface Props {
    sessionKey?: ChatSessionKey;
    editRoom?: boolean;
}

const ManagePublicRoomModal = ({ sessionKey, editRoom }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', sessionKey]),
    );

    const editValue: RoomContent = {
        attachment: null,
        imageSrc: session?.get('roomImageResource')
            ? getChatRoomAvatar(session?.get('roomImageResource'))
            : '',
        title: session?.get('name'),
        description: session?.get('roomDescription'),
    };

    const [roomContent, setRoomContent] = useState<RoomContent>(
        editRoom ? editValue : defaultValue,
    );

    const handleAttachment = (attachment: File) => {
        if (attachment) {
            const reader = new FileReader();
            reader.readAsDataURL(attachment);

            reader.onloadend = () => {
                setRoomContent((prev) => ({
                    ...prev,
                    imageSrc: reader.result.toString(),
                    attachment,
                }));
            };
        }
    };

    const history = useHistory();

    const handleSubmit = () => {
        if (editRoom) {
            const sessionDTO = fromJS({
                key: sessionKey,
                name: roomContent.title,
                roomDescription: roomContent.description,
                roomType: roomTypes.public,
            });

            dispatch(updateChatSession(sessionKey, sessionDTO));

            if (roomContent.attachment) {
                dispatch(setRoomImage(sessionKey, roomContent.attachment));
            } else if (
                !roomContent.imageSrc &&
                session?.get('roomImageResource')
            ) {
                dispatch(deleteRoomImage(sessionKey));
            }
        } else {
            const sessionDTO = fromJS({
                name: roomContent.title,
                roomDescription: roomContent.description,
                roomType: roomTypes.public,
            });

            const onSuccess = (chatSessionKey: string) =>
                history.push(
                    generatePath(routes['messages.id.settings'], {
                        id: chatSessionKey,
                    }),
                );

            dispatch(
                createRoomSession(
                    sessionDTO,
                    roomContent.attachment,
                    onSuccess,
                ),
            );
        }
    };

    const inputRef = useAutoFocus();

    return (
        <BaseModal
            open={true}
            header={
                editRoom
                    ? i18next.t('Tooltip.Nav.Title.Edit')
                    : i18next.t('Chat.Public.Create.Title')
            }
            onSubmit={handleSubmit}
            disableSubmit={!roomContent.title}
        >
            <div className="flex flex-col gap-5">
                <ImagePicker
                    imageSrc={roomContent.imageSrc}
                    removeAttachment={() =>
                        setRoomContent((prev) => ({
                            ...prev,
                            imageSrc: '',
                            attachment: null,
                        }))
                    }
                    addAttachment={(e) => {
                        handleAttachment(e.target?.files[0]);
                        e.target.value = null;
                    }}
                />

                <div className="flex w-full flex-col gap-y-1.5">
                    <label htmlFor="title" className="text-xs text-gray-800">
                        {i18next.t('Chat.Public.Create.Name.Title')}
                    </label>
                    <BaseInput
                        onChange={(e) => {
                            const title = e.target.value;

                            setRoomContent((prev) => ({
                                ...prev,
                                title,
                            }));
                        }}
                        id="title"
                        value={roomContent.title}
                        ref={
                            inputRef as React.MutableRefObject<HTMLInputElement>
                        }
                        maxLength={32}
                    />
                </div>

                <BaseTextArea
                    label={i18next.t('Chat.Public.Create.Description.Title')}
                    value={roomContent.description}
                    onChange={(e) => {
                        const description = e.target?.value;

                        setRoomContent((prev) => ({
                            ...prev,
                            description,
                        }));
                    }}
                    maxLength={255}
                    id="description"
                />
            </div>
        </BaseModal>
    );
};

export default ManagePublicRoomModal;
